import { useCallback, useEffect, useRef, useState } from "react";
import { posAPI, staffToken } from "../../services/api";
import { POS_STORAGE_KEYS, readStored, writeStored } from "../utils/storage";

const RETRY_INTERVAL_MS = 20000;

/** A sale is worth retrying when the request never reached a healthy server. */
const isRecoverable = (error) =>
  !error.response || error.response.status >= 500;

const errorMessage = (error) =>
  error.response?.data?.message ||
  error.response?.data?.error ||
  error.message ||
  "Unknown error";

/**
 * Keeps the register selling when the shop's connection drops.
 *
 * A sale that cannot reach the server is stored locally and replayed
 * automatically. Every sale carries a client_sale_id, so a replay is recognised
 * by the backend and can never charge or destock the same basket twice.
 *
 * A sale the server actively rejects (out of stock, invalid) is not retried:
 * it is surfaced to the owner instead, with the reason.
 *
 * A queued sale keeps the cashier token it was rung up under. The till locks
 * between shifts, so replaying with whatever token is current would credit a
 * sale to the next cashier — or to nobody. The token leaves storage with the
 * entry once it syncs; one older than its 12 hours is simply ignored by the
 * server, and the sale still records.
 */
export const usePosOutbox = () => {
  const [pending, setPending] = useState(() =>
    readStored(POS_STORAGE_KEYS.outbox, [])
  );
  const [failed, setFailed] = useState(() =>
    readStored(`${POS_STORAGE_KEYS.outbox}_failed`, [])
  );
  const [syncing, setSyncing] = useState(false);
  const flushing = useRef(false);
  const onSyncedRef = useRef(null);

  const persistPending = useCallback((next) => {
    setPending(next);
    writeStored(POS_STORAGE_KEYS.outbox, next);
  }, []);

  const persistFailed = useCallback((next) => {
    setFailed(next);
    writeStored(`${POS_STORAGE_KEYS.outbox}_failed`, next);
  }, []);

  /**
   * Sends a sale. Returns { status: 'recorded' | 'queued', sale? }.
   * Throws when the server refuses the sale, so the cashier is told why.
   */
  const submit = useCallback(
    async (payload) => {
      const staff = staffToken.get();

      try {
        const response = await posAPI.createSale(payload, staff);
        return { status: "recorded", sale: response.data.sale };
      } catch (error) {
        if (!isRecoverable(error)) throw error;

        const queued = [
          ...readStored(POS_STORAGE_KEYS.outbox, []),
          { payload, staffToken: staff, queuedAt: new Date().toISOString() },
        ];
        persistPending(queued);
        return { status: "queued" };
      }
    },
    [persistPending]
  );

  const flush = useCallback(async () => {
    if (flushing.current) return;

    const queue = readStored(POS_STORAGE_KEYS.outbox, []);
    if (queue.length === 0) return;

    flushing.current = true;
    setSyncing(true);

    const remaining = [...queue];
    const rejected = [];
    let syncedCount = 0;

    while (remaining.length > 0) {
      const entry = remaining[0];
      try {
        // Already paid for at the counter: never refused for a drawer that
        // has been closed since
        // `sold_at` is when it was rung up, so the server can tell which
        // drawer it belongs to rather than whichever happens to be open now
        await posAPI.createSale(
          { ...entry.payload, replayed: true, sold_at: entry.queuedAt },
          entry.staffToken,
        );
        remaining.shift();
        syncedCount += 1;
      } catch (error) {
        if (isRecoverable(error)) break; // still offline, try again later
        remaining.shift();
        rejected.push({ ...entry, error: errorMessage(error) });
      }
    }

    persistPending(remaining);

    if (rejected.length > 0) {
      persistFailed([
        ...rejected,
        ...readStored(`${POS_STORAGE_KEYS.outbox}_failed`, []),
      ]);
    }

    flushing.current = false;
    setSyncing(false);

    if (syncedCount > 0 && onSyncedRef.current) onSyncedRef.current();
  }, [persistFailed, persistPending]);

  /** Registers a callback fired after queued sales reach the server. */
  const onSynced = useCallback((callback) => {
    onSyncedRef.current = callback;
  }, []);

  const discardFailed = useCallback(
    (queuedAt) => {
      persistFailed(
        readStored(`${POS_STORAGE_KEYS.outbox}_failed`, []).filter(
          (entry) => entry.queuedAt !== queuedAt
        )
      );
    },
    [persistFailed]
  );

  useEffect(() => {
    flush();

    const interval = setInterval(flush, RETRY_INTERVAL_MS);
    window.addEventListener("online", flush);

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", flush);
    };
  }, [flush]);

  return {
    submit,
    flush,
    onSynced,
    discardFailed,
    pendingCount: pending.length,
    failed,
    syncing,
  };
};
